import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { user as userApi } from '../services/api/userClient';

interface TimezoneContextType {
  timezone: string;
  offset: number;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);
// One browser-timezone save per user at a time (StrictMode replays effects).
const timezoneDetectionInFlight = new Set<string>();

const getOffsetForTimezone = (timezone: string, instant = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);
    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const localAsUtc = Date.UTC(
      Number(byType.year),
      Number(byType.month) - 1,
      Number(byType.day),
      Number(byType.hour),
      Number(byType.minute),
      Number(byType.second),
    );
    const wholeSecondInstant = Math.floor(instant.getTime() / 1000) * 1000;
    return Math.round((wholeSecondInstant - localAsUtc) / 60000);
  } catch {
    return instant.getTimezoneOffset();
  }
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [timezone, setTimezone] = useState<string>(detectedTimezone);
  const [offset, setOffset] = useState<number>(() => getOffsetForTimezone(detectedTimezone));
  const { isAuthenticated, user, updateUser } = useAuth();
  const signedInUserIdRef = useRef(user?.id);
  signedInUserIdRef.current = user?.id;
  const signedInTimezoneRef = useRef(user?.timezone);
  signedInTimezoneRef.current = user?.timezone;

  useEffect(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const resolvedTimezone = isAuthenticated && user?.timezone ? user.timezone : browserTimezone;

    // An authenticated user's saved preference is authoritative. Posting the
    // browser timezone on every mount overwrote deliberate profile changes on
    // refresh, especially when the user selected a different working timezone.
    setTimezone(resolvedTimezone);
    setOffset(getOffsetForTimezone(resolvedTimezone));
  }, [isAuthenticated, user?.timezone]);

  useEffect(() => {
    // New accounts start on the UTC default. Save the browser zone once per user and browser;
    // the server only applies it while the default is untouched, so saved choices are kept.
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Right after sign-up or sign-in the user object may not carry a timezone yet; the server decides.
    const onDefault = user?.timezone === 'UTC' || user?.timezone == null;
    if (!isAuthenticated || !user?.id || !onDefault || !browserTimezone || browserTimezone === 'UTC') {
      return;
    }
    const storageKey = `wf:timezone-detected:${user.id}`;
    try {
      if (window.localStorage.getItem(storageKey)) return;
    } catch {
      return;
    }
    const userKey = String(user.id);
    if (timezoneDetectionInFlight.has(userKey)) return;
    timezoneDetectionInFlight.add(userKey);
    userApi
      .updateTimezone(browserTimezone, getOffsetForTimezone(browserTimezone), { onlyIfDefault: true })
      .then((result) => {
        try {
          window.localStorage.setItem(storageKey, '1');
        } catch {
          // Storage is best effort; the server-side default check still protects saved choices.
        }
        // updateUser belongs to the auth provider; apply only while the same user is signed in and has not
        // chosen a timezone in the meantime.
        const stillOnDefault = signedInTimezoneRef.current === 'UTC' || signedInTimezoneRef.current == null;
        if (signedInUserIdRef.current === user.id && stillOnDefault && result?.applied && result.timezone) {
          updateUser({ timezone: result.timezone });
        }
      })
      .catch(() => {
        // Leave the flag unset so a later visit can retry.
      })
      .finally(() => {
        timezoneDetectionInFlight.delete(userKey);
      });
  }, [isAuthenticated, user?.id, user?.timezone, updateUser]);

  return (
    <TimezoneContext.Provider value={{ timezone, offset }}>
      {children}
    </TimezoneContext.Provider>
  );
};
